import React from 'react';

import {
  TARIFF_TIME_UNIT_DAY, TARIFF_TIME_UNIT_MONTH, TARIFF_TIME_UNIT_YEAR,
} from 'src/util/constants';

/**
 * Builds a complete rate expression with proper language structure
 * This holistic approach handles the entire rate formatting including currency and time unit
 * positioning according to language-specific grammatical rules.
 * @param {import('react-intl').IntlShape} intl - UI formatter for the rate expression and period.
 * @param {import('react-intl').IntlShape} intlCurrency - Partner formatter for the monetary rate.
 * @param {number} value - The numeric rate value
 * @param {string} currencySymbol - Currency symbol (e.g., '$', '¥', '€')
 * @param {TARIFF_TIME_UNIT_DAY | TARIFF_TIME_UNIT_MONTH | TARIFF_TIME_UNIT_YEAR} timeUnit
 * @returns {React.ReactNode | string | null} Complete formatted rate expression
 * @example
 * // English (en-AU): Daily rate with AUD
 * buildValuePerTimeUnit(intl, intlCurrency, 10.50, '$', TARIFF_TIME_UNIT_DAY)
 * // Returns: "$10.50<abbr title="per day">/day</abbr>"
 * @example
 * // English (en-GB): Monthly rate with GBP
 * buildValuePerTimeUnit(intl, intlCurrency, 25.75, '£', TARIFF_TIME_UNIT_MONTH)
 * // Returns: "£25.75<abbr title="per month">/month</abbr>"
 * @example
 * // Japanese (ja-JP): Yearly rate with JPY (different structure)
 * buildValuePerTimeUnit(intl, intlCurrency, 1200, '¥', TARIFF_TIME_UNIT_YEAR)
 * // Returns: "1年¥1,200" (proper Japanese structure)
 * @example
 * // Italian (it-IT): Daily rate with EUR (different structure)
 * buildValuePerTimeUnit(intl, intlCurrency, 5.25, '€', TARIFF_TIME_UNIT_DAY)
 * // Returns: "5,25 € al giorno" (proper Italian structure)
 */
const buildValuePerTimeUnit = (intl, intlCurrency, value, currencySymbol, timeUnit) => {
  if (!intl || !intlCurrency || value === undefined || !currencySymbol || !timeUnit) {
    return null;
  }

  const formattedValue = intlCurrency.formatMessage({
    id: 'util.i18n.value_per_time_unit.formatted_value',
    defaultMessage: '{currencySymbol}{value, number, :: .00#######}',
  }, { currencySymbol, value });

  switch (timeUnit) {
    case TARIFF_TIME_UNIT_DAY: {
      const abbrTitle = intl.formatMessage({
        id: 'util.i18n.value_per_time_unit.per_day.abbr.title',
        defaultMessage: 'per day',
        description: 'Accessibility title for daily rate abbreviation',
      });

      return intl.formatMessage({
        id: 'util.i18n.value_per_time_unit.per_day.text_with_abbr',
        defaultMessage: '{formattedValue}<abbr>/day</abbr>',
      }, {
        formattedValue,
        abbr: (str) => <abbr title={abbrTitle}>{str}</abbr>,
      });
    }
    case TARIFF_TIME_UNIT_MONTH: {
      const abbrTitle = intl.formatMessage({
        id: 'util.i18n.value_per_time_unit.per_month.abbr.title',
        defaultMessage: 'per month',
      });

      return intl.formatMessage({
        id: 'util.i18n.value_per_time_unit.per_month.abbr.text',
        defaultMessage: '{formattedValue}<abbr>/month</abbr>',
      }, {
        formattedValue,
        abbr: (str) => <abbr title={abbrTitle}>{str}</abbr>,
      });
    }
    case TARIFF_TIME_UNIT_YEAR: {
      const abbrTitle = intl.formatMessage({
        id: 'util.i18n.value_per_time_unit.per_year.abbr.title',
        defaultMessage: 'per year',
      });

      return intl.formatMessage({
        id: 'util.i18n.value_per_time_unit.per_year.abbr.text',
        defaultMessage: '{formattedValue}<abbr>/year</abbr>',
      }, {
        formattedValue,
        abbr: (str) => <abbr title={abbrTitle}>{str}</abbr>,
      });
    }
    default:
      return null;
  }
};

export default buildValuePerTimeUnit;
